import { AccessManagementClient } from "@databricks/sdk-accessmanagement/v1";
import type { Credentials } from "@databricks/sdk-auth";
import type { HttpClient, HttpRequest, HttpResponse } from "@databricks/sdk-core/http";
import { ExperimentsClient } from "@databricks/sdk-experiments/v1";
import { FilesClient } from "@databricks/sdk-files/v2";
import { GenieClient } from "@databricks/sdk-genie/v1";
import { JobsClient } from "@databricks/sdk-jobs/v2";
import { ModelServingClient } from "@databricks/sdk-modelserving/v1";
import { ModelServingQueryClient } from "@databricks/sdk-modelservingquery/v1";
import type { ClientOptions } from "@databricks/sdk-options/client";
import { PipelinesClient } from "@databricks/sdk-pipelines/v2";
import { PostgresClient } from "@databricks/sdk-postgres/v1";
import { ScimClient } from "@databricks/sdk-scim/v1";
import { SecretsClient } from "@databricks/sdk-secrets/v1";
import { StatementExecutionClient } from "@databricks/sdk-statementexecution/v1";
import { SupervisorAgentsClient } from "@databricks/sdk-supervisoragents/v1";
import { CatalogsClient } from "@databricks/sdk-uc-catalogs/v1";
import { GrantsClient } from "@databricks/sdk-uc-grants/v1";
import { SchemasClient } from "@databricks/sdk-uc-schemas/v1";
import { TablesClient } from "@databricks/sdk-uc-tables/v1";
import { VolumesClient } from "@databricks/sdk-uc-volumes/v1";
import { VectorSearchClient } from "@databricks/sdk-vectorsearch/v1";
import {
  type DatabricksPrincipal,
  type DatabricksTokenProvider,
  databricksCredentials,
  databricksIdentity,
} from "./identity.js";
import { DatabricksRawProtocolClient } from "./raw-protocol-client.js";
import { databricksWorkspaceOrigin } from "./workspace-origin.js";

export interface DatabricksNativeSdkOptions {
  host: string;
  credentials: Credentials;
  tokenProvider: DatabricksTokenProvider;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  extraHeaders?:
    | Record<string, string>
    | (() => Record<string, string> | Promise<Record<string, string>>);
}

type DatabricksGeneratedSdkOptions = Omit<DatabricksNativeSdkOptions, "tokenProvider">;

/**
 * Internal native SDK client set. Fabric-facing modules depend on the narrow service they use;
 * this aggregate exists only at bundle composition and certification boundaries.
 */
export interface DatabricksNativeClients {
  accessManagement: AccessManagementClient;
  jobs: JobsClient;
  statements: StatementExecutionClient;
  pipelines: PipelinesClient;
  vectorSearch: VectorSearchClient;
  modelServing: ModelServingClient;
  modelServingQuery: ModelServingQueryClient;
  files: FilesClient;
  secrets: SecretsClient;
  genie: GenieClient;
  catalogs: CatalogsClient;
  schemas: SchemasClient;
  volumes: VolumesClient;
  grants: GrantsClient;
  tables: TablesClient;
  scim: ScimClient;
  experiments: ExperimentsClient;
  postgres: PostgresClient;
  /** Beta Supervisor Agent discovery and lifecycle through the official generated SDK. */
  supervisorAgents: SupervisorAgentsClient;
  /** Private escape hatch for reviewed protocols absent from the generated SDK. */
  rawProtocol: DatabricksRawProtocolClient;
}

/** Generated Databricks service clients exposed to application code. */
export type DatabricksSdkClients = Omit<
  DatabricksNativeClients,
  "rawProtocol" | "supervisorAgents"
>;

export interface DatabricksSdkOptions {
  host: string;
  principal: DatabricksPrincipal;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  extraHeaders?: DatabricksNativeSdkOptions["extraHeaders"];
}

/**
 * Build the official modular Databricks SDK clients under one governed identity.
 * SDK-gap protocols remain private to Fabric's explicit raw protocol adapters.
 */
export function databricksSdk(options: DatabricksSdkOptions): DatabricksSdkClients {
  const tokenProvider = databricksIdentity(options.principal);
  return stableDatabricksSdkClients(
    createDatabricksNativeClients({
      host: options.host,
      credentials: databricksCredentials(options.principal),
      tokenProvider,
      ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
      ...(options.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
      ...(options.extraHeaders ? { extraHeaders: options.extraHeaders } : {}),
    }),
  );
}

export function stableDatabricksSdkClients(clients: DatabricksNativeClients): DatabricksSdkClients {
  const { rawProtocol: _rawProtocol, supervisorAgents: _supervisorAgents, ...sdk } = clients;
  return sdk;
}

export function createDatabricksNativeClients(
  options: DatabricksNativeSdkOptions,
): DatabricksNativeClients {
  const clientOptions = generatedClientOptions(options);
  return {
    accessManagement: new AccessManagementClient(clientOptions),
    jobs: new JobsClient(clientOptions),
    statements: new StatementExecutionClient(clientOptions),
    pipelines: new PipelinesClient(clientOptions),
    vectorSearch: new VectorSearchClient(clientOptions),
    modelServing: new ModelServingClient(clientOptions),
    modelServingQuery: new ModelServingQueryClient(clientOptions),
    files: new FilesClient(clientOptions),
    secrets: new SecretsClient(clientOptions),
    genie: new GenieClient(clientOptions),
    catalogs: new CatalogsClient(clientOptions),
    schemas: new SchemasClient(clientOptions),
    volumes: new VolumesClient(clientOptions),
    grants: new GrantsClient(clientOptions),
    tables: new TablesClient(clientOptions),
    scim: new ScimClient(clientOptions),
    experiments: new ExperimentsClient(clientOptions),
    postgres: new PostgresClient(clientOptions),
    supervisorAgents: new SupervisorAgentsClient(clientOptions),
    rawProtocol: new DatabricksRawProtocolClient({
      host: options.host,
      token: options.tokenProvider,
      ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
      ...(options.extraHeaders ? { extraHeaders: options.extraHeaders } : {}),
    }),
  };
}

/** Build only the generated SCIM client used on request-scoped identity validation paths. */
export function createDatabricksScimClient(options: DatabricksGeneratedSdkOptions): ScimClient {
  return new ScimClient(generatedClientOptions(options));
}

function generatedClientOptions(options: DatabricksGeneratedSdkOptions): ClientOptions {
  return {
    host: databricksWorkspaceOrigin(options.host),
    credentials: options.credentials,
    httpClient: fabricHttpClient(options.fetchImpl ?? fetch, options.extraHeaders),
    profileOptions: { noProfile: true, disableEnv: true },
    ...(options.timeoutMs !== undefined ? { timeout: options.timeoutMs } : {}),
  };
}

function fabricHttpClient(
  fetchImpl: typeof fetch,
  extraHeaders: DatabricksNativeSdkOptions["extraHeaders"],
): HttpClient {
  return {
    async send(request: HttpRequest): Promise<HttpResponse> {
      const headers = new Headers(request.headers);
      const resolved = typeof extraHeaders === "function" ? await extraHeaders() : extraHeaders;
      for (const [key, value] of Object.entries(resolved ?? {})) headers.set(key, value);
      const body =
        request.body instanceof Uint8Array
          ? (request.body.buffer.slice(
              request.body.byteOffset,
              request.body.byteOffset + request.body.byteLength,
            ) as ArrayBuffer)
          : request.body;
      const init: RequestInit = {
        method: request.method,
        headers,
        ...(body !== undefined ? { body: body as Exclude<RequestInit["body"], undefined> } : {}),
        ...(request.signal ? { signal: request.signal } : {}),
      };
      if (request.body instanceof ReadableStream) {
        (init as RequestInit & { duplex: "half" }).duplex = "half";
      }
      // sdk-modelservingquery 0.21 serializes inference calls under `/api`,
      // while the Databricks serving data plane exposes the supported route at
      // `/serving-endpoints/:name/invocations`. Keep the generated request and
      // response codecs, but correct that one wire path at the shared transport
      // boundary until the upstream generator does so.
      const url = new URL(request.url);
      if (/^\/api\/serving-endpoints\/[^/]+\/invocations$/u.test(url.pathname)) {
        url.pathname = url.pathname.slice("/api".length);
      }
      const response = await fetchImpl(url, init);
      return {
        statusCode: response.status,
        headers: response.headers,
        body: response.body,
      };
    },
  };
}
